import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import Select from 'react-select';
import '../users/userform.css';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../services/AxiosInstance";
import { FiArrowLeft } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import { isBranchAdmin, getCurrentUserRole } from '../../utils/roleAccess';
import { FIELD_LIMITS } from '../../utils/fieldLimits';

const AddDepartment = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    branchId: "",
    departmentName: ""
  });
  const [isBranchDisabled, setIsBranchDisabled] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Helper function to safely navigate back, avoiding login page
  const navigateBackSafely = () => {
    navigate(-1);

    // Set a timeout to check if we ended up on login page
    setTimeout(() => {
      if (window.location.pathname.includes('/login')) {
        const search = searchParams.get('search') || '';
        navigate(`/task/projectlist${search ? `?search=${encodeURIComponent(search)}` : ''}`);
      }
    }, 100);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.branchId || formData.branchId === '') newErrors.branchId = 'Branch is required';
    if (!formData.departmentName.trim()) newErrors.departmentName = 'Department name is required';
    else if (formData.departmentName.length > FIELD_LIMITS.DEPARTMENT_NAME) newErrors.departmentName = `Department name cannot exceed ${FIELD_LIMITS.DEPARTMENT_NAME} characters`;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validate()) return;

    setLoading(true);
    setIsSubmitting(true);

    const userData = JSON.parse(localStorage.getItem("user"));
    
    const payload = {
      branch_id: formData.branchId,
      name: formData.departmentName,
      corporate_id: userData.corporateId,
    };
    
    try {
      const response = await axiosInstance.post(`/settings/addDepartment`, JSON.stringify(payload));

      if (!response) {
        throw new Error('Failed to submit the form');
      }

      const result = response;
      console.log('Form submitted successfully:', result);
      notifyTopRight('✅ Department created successfully!');
      
      setTimeout(() => {
        setIsSubmitting(false);
        navigateBackSafely();
      }, 2000);

    } catch (error) {
      console.error('Submission error:', error);
      if (error.status === 409) {
        notifyError('Department already exists with this branch');
      } else {
        notifyError(error.response?.data?.error || 'Failed to create department');
      }
      setIsSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        const corporateId = userData?.corporateId || userData?.id;
        const res = await axiosInstance.get(`/settings/branches?corporate_id=${corporateId}`);
        setBranches(res.data.data || res.data || []);
      } catch (err) {
        console.error("Failed to fetch branches:", err);
        notifyError('Failed to load branches');
      }
    };

    const initializeRoleBasedFields = async () => {
      try {
        const role = getCurrentUserRole();
        
        // If user is branch_admin, prefill and disable branch field
        if (isBranchAdmin(role)) {
          const userData = JSON.parse(localStorage.getItem('user'));
          const userId = userData?.id;
          
          if (userId) {
            try {
              const userDetailsRes = await axiosInstance.get(`/auth/details/${userId}`);
              const userDetails = userDetailsRes.data;
              const branchId = userDetails?.Branch?.id;
              
              if (branchId) {
                setFormData((prev) => ({ ...prev, branchId: branchId }));
                setIsBranchDisabled(true);
              }
            } catch (err) {
              console.error('Error fetching current user details:', err);
            }
          }
        }
      } catch (err) {
        console.error('Error initializing role-based fields:', err);
      }
    };

    fetchBranches();
    initializeRoleBasedFields();
  }, []);

  const notifyTopRight = (data) => {
    toast.success(`✔️ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const notifyError = (data) => {
    toast.error(`❌ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  return (
    <div>
      <button
        className="back-button"
        onClick={() => {
          if (window.history.length > 2) {
            navigate(-1);
          } else {
            navigate('/settings/departmentlist');
          }
        }}
      >
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>
      <div className="row" style={{ marginBottom: '0px !important' }}>
        <ToastContainer />
        <div className="col-xl-12 col-xxl-12">
          <div className="card">
          <div className="card-body">
              <section>
                <div className="row">
                  <div className="col-lg-6 mb-2">
                    <div className="form-group mb-3">
                      <label className="user-form-label mb-1"><strong>Branch*</strong></label>
                      <div className="user-form-select-container">
                        <Select
                          classNamePrefix="user-form-select"
                          placeholder="Select Branch"
                          isClearable
                          isDisabled={isBranchDisabled}
                          value={branches.find((branch) => branch.id === formData.branchId) 
                            ? { value: formData.branchId, label: branches.find(b => b.id === formData.branchId)?.name }
                            : null
                          }
                          onChange={(selectedOption) => {
                            setFormData((prevData) => ({
                              ...prevData,
                              branchId: selectedOption ? selectedOption.value : ''
                            }));
                          }}
                          options={branches.map((branch) => ({
                            value: branch.id,
                            label: branch.name
                          }))}
                        />
                      </div>
                      {errors.branchId && <div className="text-danger">{errors.branchId}</div>}
                    </div>
                  </div>

                  <div className="col-lg-6 mb-2">
                    <div className="form-group mb-3">
                      <label className="text-label">Department Name*</label>
                      <input
                        type="text"
                        name="departmentName"
                        className="form-control"
                        placeholder="Enter department name"
                        maxLength={FIELD_LIMITS.DEPARTMENT_NAME}
                        value={formData.departmentName}
                        onChange={handleChange}
                      />
                      {errors.departmentName && <div className="text-danger">{errors.departmentName}</div>}
                    </div>
                  </div>

                  <div className="text-end toolbar toolbar-bottom p-2">
                    <button 
                      type="button"
                      className="btn btn-cancel me-2" 
                      onClick={navigateBackSafely}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn user-form-btn sw-btn-next" 
                      onClick={handleSubmit} 
                      disabled={isSubmitting} 
                      style={{ maxWidth: '100px' }}
                    >
                      {loading ? (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      ) : null}
                      Submit
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
        <InternalFooter />
      </div>
    </div>
  );
};

export default AddDepartment;